import { forwardRef } from "react";
import { brl } from "@/lib/api";
import { Input } from "@/components/ui-kit";
import { discountAmount, discountExceedsCap, discountPercent } from "./discount";

/**
 * Campo de desconto com as duas unidades que o balcão usa.
 *
 * O alternador R$/% não é preferência de tela: é como a negociação acontece.
 * Abaixo do campo sai sempre a OUTRA leitura ("R$ 25,00 · 12,5% do subtotal"),
 * porque é ela que denuncia o erro de digitação — um zero a mais em reais passa
 * despercebido, "83% do subtotal" não passa.
 */
const DiscountField = forwardRef(function DiscountField(
  { type, onTypeChange, value, onChange, subtotal, maxPercent, disabled },
  ref,
) {
  const valor = discountAmount({ type, value, subtotal });
  const pct = discountPercent({ type, value, subtotal });
  const acima = discountExceedsCap({ type, value, subtotal, maxPercent });
  const temTeto = maxPercent !== null && maxPercent !== undefined;

  return (
    <div data-testid="pos-discount">
      <div className="flex items-center gap-2">
        {/* Segmentado: as duas unidades ficam à vista. Num <select> o vendedor
            não vê que existe a opção de porcentagem e continua fazendo a conta
            de cabeça. */}
        <div role="group" aria-label="Unidade do desconto"
          className="flex shrink-0 rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {[["valor", "R$"], ["percentual", "%"]].map(([id, rotulo]) => (
            <button key={id} type="button" onClick={() => onTypeChange(id)} disabled={disabled}
              aria-pressed={type === id} data-testid={`pos-discount-type-${id}`}
              className={`h-9 w-11 text-sm font-semibold rounded-md transition-colors ${
                type === id ? "bg-white text-brand-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}>
              {rotulo}
            </button>
          ))}
        </div>
        <Input ref={ref} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)}
          disabled={disabled} aria-label={`Desconto em ${type === "percentual" ? "porcentagem" : "reais"}`}
          aria-invalid={acima || undefined}
          placeholder={type === "percentual" ? "Desconto %  (F4)" : "Desconto R$  (F4)"}
          data-testid="pos-discount-input" mono
          className={`flex-1 min-w-0 ${acima ? "border-red-300 focus:border-red-400" : ""}`} />
      </div>

      {acima ? (
        // Erro no lugar da leitura normal: com o desconto barrado, o que importa
        // é o limite e a distância dele, não a conversão de unidade.
        <p role="alert" className="text-xs text-red-600 mt-1.5" data-testid="pos-discount-error">
          {pct.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}% passa do seu limite de{" "}
          {Number(maxPercent).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%.
          Máximo aqui: <b>{brl((subtotal * Number(maxPercent)) / 100)}</b>.
        </p>
      ) : valor > 0 ? (
        <p className="text-xs text-slate-500 mt-1.5" data-testid="pos-discount-hint">
          {type === "percentual" ? <>Equivale a <b className="text-slate-700">{brl(valor)}</b></>
            : <>Equivale a <b className="text-slate-700">{pct.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%</b> do subtotal</>}
        </p>
      ) : temTeto ? (
        <p className="text-xs text-slate-400 mt-1.5">
          Seu limite: {Number(maxPercent).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}% por venda.
        </p>
      ) : null}
    </div>
  );
});

export default DiscountField;
